/**
 * VoiceUploader Component
 *
 * Upload audio file for voice cloning with drag-and-drop.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAudioValidation } from '../../hooks/useVoices';
import { VOICE_ALLOWED_EXTENSIONS, VOICE_MAX_FILE_SIZE_MB } from '../../services/voiceApi';
import { formatFileSize } from '@/constants/fileSize';

interface VoiceUploaderProps {
  /** Called with a file that passed client-side validation. */
  onFileSelected: (file: File) => void;
  /**
   * Called whenever no valid file is selected any more - the X button, or a
   * newly picked file that failed validation. The parent owns the selected
   * audio, so it MUST be told to drop the previous file; otherwise "Create
   * Voice" uploads a file the user already removed or replaced.
   */
  onFileCleared: () => void;
  /** Called with the validation error message when a file is rejected. */
  onError: (error: string) => void;
  disabled?: boolean;
}

export const VoiceUploader: React.FC<VoiceUploaderProps> = ({
  onFileSelected,
  onFileCleared,
  onError,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { validating, validation, validateFile, resetValidation } = useAudioValidation();

  /**
   * Handle file selection.
   */
  const handleFileSelect = useCallback(
    async (file: File) => {
      // Cleanup previous URL
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      setSelectedFile(file);

      // Create preview URL
      const url = URL.createObjectURL(file);
      setAudioUrl(url);

      // Drop whatever the parent was holding: this new file supersedes it and
      // is not usable until it passes validation below.
      onFileCleared();

      // Validate file
      const result = await validateFile(file);

      if (!result.isValid) {
        onError(result.errorMessage || 'Invalid file');
      } else {
        onFileSelected(file);
      }
    },
    [audioUrl, validateFile, onError, onFileSelected, onFileCleared]
  );

  /**
   * Handle drag events.
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  /**
   * Handle file input change.
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  /**
   * Remove the selected file and reset local state.
   * The parent holds the file we handed it in `onFileSelected`, so it has to be
   * told to drop it too.
   */
  const handleReset = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setSelectedFile(null);
    setAudioUrl(null);
    resetValidation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileCleared();
  }, [audioUrl, resetValidation, onFileCleared]);

  /**
   * Open file dialog.
   */
  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="voice-uploader">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={VOICE_ALLOWED_EXTENSIONS.join(',')}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Drop zone */}
      {!selectedFile && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-colors duration-200
            ${
              isDragging
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className="text-4xl mb-4">🎤</div>
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            {t('voiceClone.dropZoneTitle')}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {t('voiceClone.dropZoneSubtitle', {
              formats: VOICE_ALLOWED_EXTENSIONS.join(', ').toUpperCase(),
              maxSize: VOICE_MAX_FILE_SIZE_MB,
            })}
          </p>
        </div>
      )}

      {/* Selected file preview */}
      {selectedFile && (
        <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-medium text-gray-700 dark:text-gray-300">{selectedFile.name}</p>
              <p className="text-sm text-gray-500">
                {formatFileSize(selectedFile.size)}
                {validation?.durationSeconds && (
                  <span className="ml-2">- {validation.durationSeconds.toFixed(1)}s</span>
                )}
              </p>
            </div>
            <button
              onClick={handleReset}
              className="text-gray-500 hover:text-red-500"
              title={t('voiceClone.removeFile')}
            >
              X
            </button>
          </div>

          {/* Validation status */}
          {validating && (
            <div className="text-sm text-blue-500 mb-2">{t('voiceClone.validating')}...</div>
          )}

          {validation && !validation.isValid && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded mb-2">
              {validation.errorMessage}
            </div>
          )}

          {validation && validation.isValid && validation.warningMessage && (
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded mb-2">
              {validation.warningMessage}
            </div>
          )}

          {validation && validation.isValid && (
            <div className="p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded mb-2">
              {t('voiceClone.fileValid')}
            </div>
          )}

          {/* Audio preview */}
          {audioUrl && <audio src={audioUrl} controls className="w-full mt-2" />}
        </div>
      )}
    </div>
  );
};

export default VoiceUploader;
